"use client"

import React, {
  ComponentPropsWithoutRef,
  ComponentRef,
  forwardRef,
} from "react"
import { tv } from "tailwind-variants"
import { ArrowUp } from "../../icons"
import { Nullable } from "../../types/utils"
import { classNames } from "../../utils/classNames"
import { AnimatedNumber } from "../AnimatedNumber"
import { Flex } from "../Flex"
import { FlexCenter } from "../FlexCenter"
import { NumberDisplay } from "../NumberDisplay"

type StatChangeBaseProps = {
  /**
   * This is in decimal format. ex. 0.1 for 10%
   */
  change: Nullable<number>

  animated?: boolean
  showPositiveSign?: boolean
}

const statChangeVariants = tv({
  base: "font-mono",
  variants: {
    variant: {
      zero: "",
      // TODO: fix when colors improvements changes are made
      positive: "text-success",
      negative: "text-error",
    },
  },
})

export type StatChangeProps = StatChangeBaseProps &
  Omit<ComponentPropsWithoutRef<"span">, "children">

export const StatChangeBase = forwardRef<ComponentRef<"span">, StatChangeProps>(
  function StatChange(
    { className, animated, change, showPositiveSign = true, ...rest },
    ref,
  ) {
    const Comp = animated ? AnimatedNumber : NumberDisplay

    const positive = typeof change === "number" && change > 0
    const negative = typeof change === "number" && change < 0

    return (
      <Comp
        bounded={false}
        className={classNames(
          statChangeVariants({
            variant: positive ? "positive" : negative ? "negative" : "zero",
          }),
          className,
        )}
        display="percent"
        prefix={positive && showPositiveSign ? "+" : undefined}
        ref={ref}
        value={change}
        {...rest}
      />
    )
  },
)

const statChangeCalloutVariants = tv({
  base: "overflow-hidden rounded-full px-2 py-0.5 font-medium",
  variants: {
    variant: {
      zero: "bg-bg-additional-1-transparent",
      positive: "bg-success-transparent-1",
      negative: "bg-error-transparent-1",
    },
    animated: {
      true: "transition-[background-color] duration-300",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: ["positive", "negative"],
      className: "gap-1.5",
    },
  ],
})

const statChangeCalloutIconVariants = tv({
  variants: {
    variant: {
      zero: "ml-[-9px] -translate-x-full opacity-0",
      positive: "fill-success",
      negative: "rotate-180 fill-error",
    },
    animated: {
      true: "transition-[fill,transform,opacity,margin-left] duration-500 ease-out-quint",
      false: "",
    },
  },
})

type StatChangeCalloutInheritedProps = "change" | "animated"

export type StatChangeCalloutProps = Pick<
  StatChangeProps,
  StatChangeCalloutInheritedProps
> & {
  overrides?: {
    StatChange?: Partial<Omit<StatChangeProps, StatChangeCalloutInheritedProps>>
  }
} & ComponentPropsWithoutRef<typeof Flex>

export const StatChangeCallout = forwardRef<
  ComponentRef<typeof Flex>,
  StatChangeCalloutProps
>(function StatChangeCallout(
  { className, animated, overrides, change, ...rest },
  ref,
) {
  const positive = typeof change === "number" && change > 0
  const negative = typeof change === "number" && change < 0

  const variant = positive ? "positive" : negative ? "negative" : "zero"

  return (
    <FlexCenter
      className={classNames(
        statChangeCalloutVariants({ variant, animated }),
        className,
      )}
      ref={ref}
      {...rest}
    >
      <ArrowUp
        className={statChangeCalloutIconVariants({ variant, animated })}
        size={9}
      />
      <StatChangeBase
        animated={animated}
        change={change}
        showPositiveSign={false}
        {...overrides?.StatChange}
      />
    </FlexCenter>
  )
})

export const StatChange = Object.assign(StatChangeBase, {
  Callout: StatChangeCallout,
})
